import type { ModuleID, VersionString, ViewID } from "@bestowinc/tinkertoy-core";

import { isModuleLoadError } from "../module-loader/load-error";
import { ShellLoaderError } from "../router/shell-loader-error";
import { TinkertoyActionError } from "../actions/action-errors";
import { isViewLoadError } from "../view-loader/load-error";

export { isModuleLoadError } from "../module-loader/load-error";
export { isViewLoadError } from "../view-loader/load-error";

function hasStringCode(value: unknown): value is { code: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    "code" in value &&
    typeof Reflect.get(value, "code") === "string"
  );
}

export function isAbortError(value: unknown): boolean {
  return value instanceof DOMException && value.name === "AbortError";
}

export function resolveErrorCode(value: unknown): string {
  if (ShellLoaderError.isInstance(value)) {
    return value.code;
  }
  if (TinkertoyActionError.isInstance(value)) {
    return value.code ?? "action_error";
  }
  if (isModuleLoadError(value)) {
    return value.code;
  }
  if (isViewLoadError(value)) {
    return value.code;
  }
  if (hasStringCode(value)) {
    return value.code;
  }
  if (value instanceof Error) {
    return value.name;
  }
  return "unknown";
}

export function resolveFailureMessage(value: unknown): string {
  if (ShellLoaderError.isInstance(value)) {
    return value.message;
  }
  if (TinkertoyActionError.isInstance(value)) {
    return value.message;
  }
  if (isModuleLoadError(value)) {
    if (value.code === "INVALID_MODULE") {
      return value.reason;
    }
    return value.cause.message;
  }
  if (isViewLoadError(value)) {
    if (value.code === "INVALID_VIEW") {
      return value.reason;
    }
    return value.cause.message;
  }
  if (value instanceof Error) {
    return value.message;
  }
  return "An unexpected error occurred.";
}

/** Returns an Error suitable for noticeError / captureException (preserves stack when possible). */
export function resolveFailureError(value: unknown, fallbackMessage: string): Error {
  if (value instanceof Error) {
    return value;
  }
  if (isModuleLoadError(value)) {
    if (value.code === "INVALID_MODULE") {
      return new Error(value.reason);
    }
    return value.cause;
  }
  if (isViewLoadError(value)) {
    if (value.code === "INVALID_VIEW") {
      return new Error(value.reason);
    }
    return value.cause;
  }
  return new Error(fallbackMessage);
}

export type FailureIdentity = {
  errorCode: string;
  viewId?: ViewID;
  viewVersion?: VersionString;
  moduleId?: ModuleID;
  moduleVersion?: VersionString;
  actionScope?: "module" | "view_interaction";
  actionName?: string;
  flowId?: string;
  branchId?: string;
};

export function extractFailureIdentity(value: unknown): FailureIdentity {
  const identity: FailureIdentity = {
    errorCode: resolveErrorCode(value),
  };

  if (isModuleLoadError(value)) {
    identity.moduleId = value.moduleId;
    identity.moduleVersion = value.version;
  }
  if (isViewLoadError(value)) {
    identity.viewId = value.viewId;
    identity.viewVersion = value.version;
  }
  if (ShellLoaderError.isInstance(value)) {
    identity.moduleId = value.errorContext?.moduleId;
  }

  return identity;
}
